import { isEqual } from 'lodash-es';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { removeEmptyParams } from '@/stores/filters/helpers';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useFilters } from '@/stores/filters/filters';
import { getAllTemplates } from '@/api/settings';
import {
  recruitJsonMakeDefault,
  recruitJsonDeleteTemplate,
  recruitJsonMakeHidden,
} from '@/api/recruitJson';

const PAGE_SIZE = 20;
const DEFAULT_SORT = '-created_at';

const getTemplateId = (template) => (template.created_by === 'System' ? 0 : template.created_by_id);

export const useTemplates = defineStore('templates', () => {
  // Other Stores
  const { setLoading } = useAppLoading();
  const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();
  const routeStore = useAppRoute();
  const routeStoreQuery = routeStore.route.query;

  const { fields, myJobsEnabled } = storeToRefs(useFilters());

  // State
  const templates = ref([]);
  const page = ref(routeStoreQuery?.page ? parseInt(routeStoreQuery?.page, 10) : 1);
  const sort = ref(routeStoreQuery?.sort || '-created_at');
  const isActiveOnly = ref(routeStoreQuery?.active_only !== 'false');
  const searchTerm = ref(routeStoreQuery?.q || '');

  // Getters
  const sortDirection = computed(() => (sort.value.startsWith('-') ? 'desc' : 'asc'));
  const selectedSort = computed(() => sort.value.replace('-', ''));

  const filteredTemplates = computed(() => {
    if (!templates.value.length) {
      return [];
    }
    return templates.value.filter((template) => {
      let keep = true;

      if (isActiveOnly.value && template.is_hidden) {
        keep = false;
      }

      if (searchTerm.value.length) {
        const search = searchTerm.value.toLowerCase();
        const searchFields = ['title', 'created_by'];
        const found = searchFields.some((field) => {
          const value = template[field] ? template[field].toLowerCase() : '';
          return value.includes(search);
        });

        if (!found) {
          keep = false;
        }
      }

      if (
        myJobsEnabled.value
          && getTemplateId(template) !== useAppAuthData().authData.userId
      ) {
        keep = false;
      }

      if (fields.value.length) {
        fields.value.forEach((filter) => {
          if (filter.value.length && filter.id === 'users') {
            const filterValues = filter.value.map((val) => val.id);

            if (!filterValues.includes(getTemplateId(template))) {
              keep = false;
            }
          }
        });
      }

      return keep;
    });
  });

  const totalPages = computed(() => Math.ceil(filteredTemplates.value.length / PAGE_SIZE));

  const sortedTemplates = computed(() => {
    if (!filteredTemplates.value.length) {
      return [];
    }

    return [...filteredTemplates.value].sort((a, b) => {
      const isDate = ['created_at', 'last_edited'].includes(selectedSort.value);
      if (sortDirection.value === 'asc') {
        if (isDate) {
          return parseInt(a[selectedSort.value], 10) > parseInt(b[selectedSort.value], 10) ? 1 : -1;
        }
        return a[selectedSort.value] > b[selectedSort.value] ? 1 : -1;
      }

      if (isDate) {
        return parseInt(a[selectedSort.value], 10) < parseInt(b[selectedSort.value], 10) ? 1 : -1;
      }

      return a[selectedSort.value] < b[selectedSort.value] ? 1 : -1;
    });
  });

  const paginatedTemplates = computed(() => {
    if (!sortedTemplates.value.length) {
      return [];
    }

    const start = (page.value - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    return sortedTemplates.value.slice(start, end);
  });

  // Mutations
  const updateTemplates = (newTemplates) => {
    templates.value = newTemplates;
  };

  const updatePage = (newPage) => {
    page.value = newPage;
  };

  const updateSearchTerm = (newSearchTerm) => {
    searchTerm.value = newSearchTerm;
  };

  const resetPage = () => {
    page.value = 1;
  };

  const resetState = () => {
    templates.value = [];
    page.value = 1;
    sort.value = DEFAULT_SORT;
    isActiveOnly.value = true;
    searchTerm.value = '';
  };

  const updateIsActiveOnly = (newIsActiveOnly, router) => {
    isActiveOnly.value = newIsActiveOnly;
    resetPage();

    router.replace({
      query: { ...routeStore.route.query, active_only: newIsActiveOnly },
    }).catch(() => true);
  };

  const submitSort = ({ optionId, router }) => {
    sort.value = optionId;
    resetPage();

    router.replace({
      query: { ...routeStore.route.query, sort: optionId, page: 1 },
    }).catch(() => true);
  };

  // Actions
  const loadTemplates = async ({
    routeQuery,
    router,
    page: newPage,
    selectedMyJobs,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let params = routeQuery || {};

      if (!params.sort) {
        params.sort = DEFAULT_SORT;
      } else if (sort.value) {
        params = { ...params, sort: sort.value };
      }

      params.page = newPage || undefined;

      const { templates: loadedTemplates } = await getAllTemplates();

      const userFilterOptions = [];
      loadedTemplates.forEach((template) => {
        const templateId = getTemplateId(template);

        if (!userFilterOptions.find((option) => option.id === templateId)) {
          userFilterOptions.push({
            id: templateId,
            label: template.created_by,
          });
        }
      });

      const usersParamsIds = Array.isArray(params.users)
        ? (params.users || []).map((id) => parseInt(id, 10))
        : parseInt(params.users, 10);

      const newFilters = [{
        id: 'users',
        label: 'Users',
        options: userFilterOptions,
        async: false,
        alwaysOn: true,
        position: 'column_1',
        type: 'flatlist',
        category: 'basic',
        value: userFilterOptions
          .filter((option) => (Array.isArray(usersParamsIds)
            ? usersParamsIds.includes(option.id)
            : usersParamsIds === option.id)),
      }];

      useFilters().updateFilters({
        filters: newFilters,
        myJobsEnabled: { value: selectedMyJobs },
      });

      updateTemplates(loadedTemplates);

      updatePage(newPage);

      if (
        router
        && !isEqual(
          removeEmptyParams(routeStore.route?.query),
          removeEmptyParams(routeQuery),
        )
      ) {
        const newQuery = params;

        if (selectedMyJobs) {
          newQuery.owned = selectedMyJobs;
        }

        router.replace({
          query: newQuery,
        }).catch(() => true);
      }
    } catch (error) {
      addErrorSnackbar({ text: `Load templates - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false, listLoading: false, sidebarLoading: false });
    }
  };

  const templatesFilterUpdate = ({
    router,
    selectedFilters,
    page: newPage,
  }) => {
    const pageToUse = newPage !== page.value ? newPage : 1;
    let params = routeStore.route.query || {};

    if (!params?.sort) {
      params.sort = DEFAULT_SORT;
    } else if (sort.value) {
      params = { ...params, sort: sort.value };
    }

    params.page = pageToUse || undefined;

    let newFilters = [];
    newFilters = fields.value.map((filter) => {
      if (filter.id === 'users') {
        return {
          ...filter,
          value: selectedFilters.users
            ? filter.options.filter((option) => selectedFilters.users.includes(option.id))
            : [],
        };
      }

      return filter;
    });

    useFilters().updateFilters({
      filters: newFilters,
    });

    updatePage(pageToUse);

    if (router) {
      const {
        sort: querySort = sort.value,
        active_only: activeOnly = isActiveOnly.value,
      } = params;

      const newQuery = {
        page: pageToUse,
        sort: querySort,
        active_only: activeOnly,
        ...selectedFilters,
      };

      if (myJobsEnabled.value) {
        newQuery.owned = myJobsEnabled.value;
      }

      router.replace({
        query: newQuery,
      }).catch(() => true);
    }
  };

  const makeDefault = async (templateId, title) => {
    setLoading({ listLoading: true });

    try {
      const { success } = await recruitJsonMakeDefault({ jobId: templateId });

      if (success) {
        const updatedTemplates = templates.value.map((tmp) => {
          if (tmp.id === templateId) {
            return { ...tmp, is_default: true };
          }

          return { ...tmp, is_default: false };
        });

        updateTemplates(updatedTemplates);

        resetPage();

        addSuccessSnackbar({
          text: `${title} has been set to the new default`,
        });
      } else {
        addErrorSnackbar({ text: 'Failed to set template as default' });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Failed to set template as default - ${error.message}` });
      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const deleteTemplate = async (template) => {
    setLoading({ listLoading: true });

    try {
      const { success } = await recruitJsonDeleteTemplate({ jobId: template.id });

      if (success) {
        const updatedTemplates = templates.value.filter((tmp) => tmp.id !== template.id);

        updateTemplates(updatedTemplates);

        resetPage();

        addSuccessSnackbar({
          text: `${template.title} has been deleted`,
        });
      } else {
        addErrorSnackbar({ text: 'Failed to delete the template' });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Failed to delete the template - ${error.message}` });
      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const toggleIsHidden = async (id, isHidden) => {
    setLoading({ listLoading: true });

    try {
      const newIsHidden = !isHidden;
      const { success } = await recruitJsonMakeHidden({ jobId: id, isHidden: newIsHidden });

      if (success) {
        const updatedTemplates = templates.value.map((template) => {
          if (template.id === id) {
            return { ...template, is_hidden: newIsHidden };
          }

          return template;
        });

        updateTemplates(updatedTemplates);

        addSuccessSnackbar({
          text: 'The template status is updated',
        });
      } else {
        addErrorSnackbar({ text: 'Could not change template status' });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Could not change template status - ${error.message}` });
      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  return {
    // State
    templates,
    sort,
    isActiveOnly,
    page,
    searchTerm,

    // Getters
    sortDirection,
    selectedSort,
    filteredTemplates,
    sortedTemplates,
    paginatedTemplates,
    totalPages,

    // Mutations
    updateTemplates,
    submitSort,
    updateIsActiveOnly,
    updatePage,
    updateSearchTerm,
    resetState,

    // Actions
    loadTemplates,
    makeDefault,
    deleteTemplate,
    toggleIsHidden,
    templatesFilterUpdate,
  };
});
